/**
 * Contract C-4 — one stamp, one parser (agnostic ingest AG-4).
 *
 * `data-pb-behaviour` holds space-separated tokens, each `<id>` or
 * `<id>:<variant>`, both kebab-case, no colons inside either part.
 * `parseBehaviourStamp` is the ONLY reader. Nothing anywhere may use `~=` on
 * this attribute: `[data-pb-behaviour~="tabs"]` matches the token `tabs` and
 * NOT `tabs:webflow`, so a selector written that way silently stops matching
 * the moment a variant is added — which is the shape of bug that survives
 * review because it looks correct and fails only later.
 *
 * The attribute is PERSISTED. It survives `stripAllBuilderAttrs` by exact-name
 * carve-out, because it is the emitted product: the exported site's runtime
 * finds its host through it. `data-pb-bound-<id>` is the opposite — transient,
 * written by a runtime at bind time, and stripped on save AND on export, so new
 * DOM always binds and a stale marker can never make a runtime skip a host it
 * has not actually bound.
 *
 * This module reads the registry; the registry imports nothing. Consumers get
 * their answers from here so a second interpretation of the same data cannot
 * appear somewhere else.
 */

import { BEHAVIOUR_PATTERNS } from './behaviour-patterns.js';

/** The persisted stamp. Carved out of `stripAllBuilderAttrs` by exact name. */
export const BEHAVIOUR_ATTR = 'data-pb-behaviour';

/** Transient bind marker prefix. Stripped on save and on export. */
export const BEHAVIOUR_BOUND_PREFIX = 'data-pb-bound-';

/** Set by the saved-preview/static-capture path; gates any runtime "wake". */
export const STATIC_CAPTURE_ATTR = 'data-pb-static-capture';

const KEBAB = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/**
 * Is this a legal id or variant token half? Exported so the registry's own
 * shape test and the parser agree by construction rather than by two regexes.
 */
export function isKebabToken(value) {
  return typeof value === 'string' && KEBAB.test(value);
}

/**
 * THE reader. Returns `[{ id, variant }]` — `variant` is `null` for a bare id.
 *
 * Tolerant of junk by design: an unparseable token is DROPPED, not thrown on.
 * This runs in the renderer against HTML that may have been hand-edited, pasted
 * from another project, or written by a version of the parser that no longer
 * exists; refusing the whole attribute because one token is malformed would take
 * a working widget down with a typo. What it must never do is invent a token,
 * so anything it cannot read in full is discarded rather than guessed at.
 *
 * Order is preserved and duplicates are collapsed on `id:variant`, so a
 * host stamped twice by two passes yields one entry.
 */
export function parseBehaviourStamp(value) {
  const out = [];
  const seen = new Set();
  for (const raw of String(value || '').split(/\s+/)) {
    const token = raw.trim();
    if (!token) continue;
    const parts = token.split(':');
    if (parts.length > 2) continue;
    const [id, variant = null] = parts;
    if (!isKebabToken(id)) continue;
    if (variant !== null && !isKebabToken(variant)) continue;
    const key = `${id}:${variant || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id, variant });
  }
  return out;
}

/** Serialize back to attribute form. Inverse of the parser for legal input. */
export function formatBehaviourStamp(entries) {
  return (entries || [])
    .filter((e) => e && isKebabToken(e.id) && (!e.variant || isKebabToken(e.variant)))
    .map((e) => (e.variant ? `${e.id}:${e.variant}` : e.id))
    .join(' ');
}

const BY_ID = new Map(BEHAVIOUR_PATTERNS.map((entry) => [entry.id, entry]));

/** Registry row for an id, or null. */
export function behaviourEntry(id) {
  return BY_ID.get(id) || null;
}

/** Registry variant row, or null. */
export function behaviourVariant(id, variantId) {
  const entry = BY_ID.get(id);
  if (!entry) return null;
  return (entry.variants || []).find((v) => v.id === variantId) || null;
}

/**
 * Does a stamped `{id, variant}` name something a runtime can actually
 * reconstruct? DECLARED is not SHIPPED: a registry row exists for every
 * behaviour the corpus carries, and only some of them have a runtime file.
 *
 * A variant may opt out on its own (`runtime: null` on the variant) even when
 * its entry ships one — that is how AG-4 ships `tabs:webflow` while `tabs:aria`,
 * fifteen times more common in the corpus, stays declared and unstamped until
 * something has exercised it.
 */
export function behaviourIsShipped(id, variantId = null) {
  const entry = BY_ID.get(id);
  if (!entry || entry.support !== 'runtime' || !entry.runtime) return false;
  if (variantId === null) return true;
  const variant = behaviourVariant(id, variantId);
  if (!variant) return false;
  // A NATIVE variant is supported with no runtime and no stamp: `<details>`
  // opens itself. Supported means what the census and the support report
  // count; the stamp pass and the loader both skip it (AG-12).
  if (variant.native) return true;
  return !('runtime' in variant) || variant.runtime !== null;
}

/** Every id with a runtime file, sorted. The loader's work list. */
export function shippedBehaviourIds() {
  return BEHAVIOUR_PATTERNS.filter((e) => e.support === 'runtime' && e.runtime)
    .map((e) => e.id)
    .sort();
}

/** The runtime filename for an id (`tabs.js`), or null. */
export function behaviourRuntimeFile(id) {
  const entry = BY_ID.get(id);
  return entry && entry.support === 'runtime' && entry.runtime ? entry.runtime : null;
}

/**
 * The serialized `__pbBehaviour` payload for one stamped host: everything the
 * runtime needs, and every selector it will use.
 *
 * The runtime contract forbids selector literals in runtime source precisely so
 * this is the only place a selector can come from — which means a site whose
 * markup differs is a REGISTRY change, reviewable as data, rather than an edit
 * to executable code that ships to every exported site.
 */
export function behaviourRuntimeConfig(id, variantId) {
  const entry = BY_ID.get(id);
  const variant = behaviourVariant(id, variantId);
  if (!entry || !variant) return null;
  return {
    id,
    variant: variantId,
    controlsScope: variant.controlsScope || 'host',
    legacyGuards: [...(entry.legacyGuards || [])],
    stateClasses: [...(variant.stateClasses || [])],
    config: variant.config ? JSON.parse(JSON.stringify(variant.config)) : null
  };
}

// ---------------------------------------------------------------------------
// AG-14: registry-declared transitions, for constrained fidelity claims
// ---------------------------------------------------------------------------
// An entry may declare `transitions: { <name>: { effects: [...], evidence } }`:
// the differences its runtime legitimately produces between the crawl and the
// builder render, per ROLE (an element's part in the widget), channel and
// property, each with a RELATION the fidelity gate verifies. The relation
// semantics live in `tests/helpers/builder-context-compare.js`; this module
// only knows the vocabulary, so the registry cannot name a relation nothing
// checks.

/** Relations a transition effect may declare. */
export const TRANSITION_RELATIONS = Object.freeze([
  // every matched geometry delta of one host shifts by the same signed amount
  // as the anchor role's delta
  'uniform-shift',
  // a computed `transform` differs only in its x translation
  'translate-x-only',
  // a class list differs only by the variant's declared state classes
  'state-only'
]);

/** Roles an effect may name; resolved from the variant config by `claimRolesFor`. */
export const CLAIM_ROLES = Object.freeze([
  'host',
  'track',
  'slide',
  'bullet',
  'control',
  'pagination'
]);

/** A declared transition for an id, or null. */
export function behaviourTransition(id, name) {
  const entry = BY_ID.get(id);
  return (entry && entry.transitions && entry.transitions[name]) || null;
}

/**
 * The role selectors for a stamped variant — the SAME config selectors its
 * runtime binds with, so "the track" means the element the runtime moves.
 * Null for a variant whose entry declares no transitions.
 */
export function claimRolesFor(id, variantId) {
  const entry = BY_ID.get(id);
  const variant = behaviourVariant(id, variantId);
  if (!entry || !entry.transitions || !variant || !variant.config) return null;
  const cfg = variant.config;
  const control = [cfg.prev, cfg.next, cfg.controlsByAria ? cfg.ariaControlled : null]
    .filter(Boolean)
    .join(', ');
  return {
    controlsScope: variant.controlsScope || 'host',
    roles: {
      track: cfg.track || null,
      slide: cfg.slide || null,
      bullet: cfg.bullet ? `.${cfg.bullet}` : null,
      control: control || null,
      pagination: cfg.pagination || null
    },
    // What the runtime may own OUTSIDE the host when controlsScope is
    // 'ancestor': the nearest ancestor level where these match. The aria link
    // (an element whose aria-controls names the track) is resolved on its own,
    // never by walking — `[aria-controls]` alone would claim a tab.
    scopeSelectors: {
      control: [cfg.prev, cfg.next].filter(Boolean).join(', ') || null,
      pagination: cfg.pagination || null
    }
  };
}

/** `id:variant` → role selectors, for every variant whose entry declares transitions. */
export function claimRolesTable() {
  const out = {};
  for (const entry of BEHAVIOUR_PATTERNS) {
    if (!entry.transitions) continue;
    for (const variant of entry.variants || []) {
      const table = claimRolesFor(entry.id, variant.id);
      if (table) out[`${entry.id}:${variant.id}`] = table;
    }
  }
  return out;
}

/**
 * Every distinct behaviour id stamped anywhere in an HTML string.
 *
 * Deliberately a STRING scan, not a DOM parse. Three of its callers run where
 * there is no DOM — the exporters and the loader decide what to ship before any
 * document exists — and MCP's rule applies with full force here: re-serialising
 * stored section HTML to ask it a question is how linkedom's 25% round-trip
 * failure rate gets into the corpus. The regex reads the attribute; it never
 * rewrites anything.
 */
const ATTR_SCAN = new RegExp(`${BEHAVIOUR_ATTR}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'gi');
export function behaviourIdsInHtml(html) {
  const ids = new Set();
  const text = String(html || '');
  if (!text.includes(BEHAVIOUR_ATTR)) return [];
  ATTR_SCAN.lastIndex = 0;
  let m;
  while ((m = ATTR_SCAN.exec(text)) !== null) {
    for (const { id } of parseBehaviourStamp(m[2] ?? m[3] ?? '')) ids.add(id);
  }
  return [...ids].sort();
}

/** Every `{id, variant}` stamped in an HTML string, deduped. */
export function behaviourStampsInHtml(html) {
  const seen = new Map();
  const text = String(html || '');
  if (!text.includes(BEHAVIOUR_ATTR)) return [];
  ATTR_SCAN.lastIndex = 0;
  let m;
  while ((m = ATTR_SCAN.exec(text)) !== null) {
    for (const entry of parseBehaviourStamp(m[2] ?? m[3] ?? '')) {
      seen.set(`${entry.id}:${entry.variant || ''}`, entry);
    }
  }
  return [...seen.values()];
}

/**
 * Does this section need a runtime on the page — because it carries section JS,
 * a behaviour stamp, or both?
 *
 * THE reason this exists as one function: `ui/shell.js` guards the full-render
 * path on `section.js` in three places and `message-handler.js` decides there
 * whether an `innerHTML` swap is safe. A stamped section with EMPTY `js` looks
 * inert to every one of those tests, so it would take the surgical-sync path,
 * have its markup replaced under a bound runtime, and lose its listeners with
 * nothing reporting a problem. One predicate, four call sites.
 */
export function sectionHasRuntime(section) {
  if (!section) return false;
  if (typeof section.js === 'string' && section.js.trim()) return true;
  return behaviourIdsInHtml(section.html).length > 0;
}

/**
 * Every state class any stamped behaviour in this HTML can toggle.
 *
 * css-prune keeps only what the captured markup uses, and a behaviour's classes
 * are dormant BY DEFINITION — the crawl froze the widget in one state, so the
 * other states' classes appear nowhere. Pruning them deletes exactly the rules
 * the reconstruction needs, and the symptom is a tab that switches with no
 * visible change.
 */
export function behaviourStateClasses(html) {
  const classes = new Set();
  for (const { id, variant } of behaviourStampsInHtml(html)) {
    const entry = BY_ID.get(id);
    if (!entry) continue;
    const variants = variant
      ? (entry.variants || []).filter((v) => v.id === variant)
      : entry.variants || [];
    for (const v of variants) for (const c of v.stateClasses || []) classes.add(c);
  }
  return [...classes].sort();
}

// ---------------------------------------------------------------------------
// Import-time assertions
// ---------------------------------------------------------------------------
// The registry is hand-edited data, and every rule above is only true if the
// data obeys the contract. Checking at import means a malformed row fails the
// first thing that loads it — the parser, a test, the renderer — rather than
// producing a stamp nobody can read. Cheap: six entries.

for (const entry of BEHAVIOUR_PATTERNS) {
  if (!isKebabToken(entry.id)) {
    throw new Error(`behaviour-patterns: id ${JSON.stringify(entry.id)} is not kebab-case (C-4)`);
  }
  if (!['runtime', 'static', 'none'].includes(entry.support)) {
    throw new Error(`behaviour-patterns: ${entry.id} has an unknown support ${entry.support}`);
  }
  if (entry.support === 'runtime' && !entry.runtime) {
    throw new Error(
      `behaviour-patterns: ${entry.id} claims support:'runtime' with no runtime file`
    );
  }
  if (!entry.evidence) {
    throw new Error(`behaviour-patterns: ${entry.id} has no evidence — not admissible`);
  }
  const seen = new Set();
  for (const variant of entry.variants || []) {
    if (!isKebabToken(variant.id)) {
      throw new Error(`behaviour-patterns: ${entry.id}:${variant.id} is not kebab-case (C-4)`);
    }
    if (seen.has(variant.id)) {
      throw new Error(`behaviour-patterns: ${entry.id} declares ${variant.id} twice`);
    }
    seen.add(variant.id);
    if (!variant.evidence) {
      throw new Error(`behaviour-patterns: ${entry.id}:${variant.id} has no evidence`);
    }
    // A stampable variant must be able to say WHERE it goes and WHAT the
    // runtime gets. Without both, the stamp pass would mark a host the runtime
    // cannot act on — a marker that proves nothing, which C-8 forbids. "Where"
    // is a host selector for the selector pass, or `stampedBy: 'nav-shape'` for
    // the variants the parser's nav-shape pass places (AG-6): those are decided
    // by what a site's CSS styles, which no selector can express.
    if (behaviourIsShipped(entry.id, variant.id) && !variant.native) {
      const placed = !!variant.host || variant.stampedBy === 'nav-shape';
      if (!placed || !variant.config) {
        throw new Error(
          `behaviour-patterns: ${entry.id}:${variant.id} is shipped but declares no host/config`
        );
      }
    }
  }
  // AG-14: a transition is a claim vocabulary. Every effect names a role the
  // config can resolve, a channel the gate diffs, and a relation the gate can
  // verify; a `uniform-shift` needs an anchor to shift relative to.
  for (const [name, transition] of Object.entries(entry.transitions || {})) {
    if (!isKebabToken(name)) {
      throw new Error(
        `behaviour-patterns: ${entry.id} transition ${JSON.stringify(name)} is not kebab-case`
      );
    }
    if (!transition || !transition.evidence) {
      throw new Error(`behaviour-patterns: ${entry.id} transition ${name} has no evidence`);
    }
    if (!Array.isArray(transition.effects) || transition.effects.length === 0) {
      throw new Error(`behaviour-patterns: ${entry.id} transition ${name} declares no effects`);
    }
    for (const effect of transition.effects) {
      if (!CLAIM_ROLES.includes(effect.role)) {
        throw new Error(
          `behaviour-patterns: ${entry.id}.${name} names an unknown role ${effect.role}`
        );
      }
      if (!['geometry', 'computed', 'classes'].includes(effect.channel)) {
        throw new Error(
          `behaviour-patterns: ${entry.id}.${name} names a channel a claim cannot explain: ${effect.channel}`
        );
      }
      if (typeof effect.property !== 'string' || !effect.property) {
        throw new Error(`behaviour-patterns: ${entry.id}.${name} effect has no property`);
      }
      if (!TRANSITION_RELATIONS.includes(effect.relation)) {
        throw new Error(
          `behaviour-patterns: ${entry.id}.${name} names a relation nothing verifies: ${effect.relation}`
        );
      }
    }
    if (
      transition.effects.some((e) => e.relation === 'uniform-shift') &&
      !transition.effects.some((e) => e.anchor && e.relation === 'uniform-shift')
    ) {
      throw new Error(
        `behaviour-patterns: ${entry.id}.${name} declares uniform-shift with no anchor`
      );
    }
  }
}
